import React, {memo} from "react";
import dayjs from "dayjs";
import {useTranslation} from "react-i18next";
import {Box, Paper, Stack, Typography} from "@mui/material";
import {AggregatedLayerProps, isRasterLayerType, RasterLayerProps} from "../../utils/layersUtils";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {ChosenLayerProps} from "../../types/mapSelectedLayers";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {DateFormatType} from "../../types/domain/dateFormat";
import {TFunction} from "i18next";
import {HelpPopover} from "../base/HelpPopover.tsx";
import {RasterLayerType} from "../../types/domain/rasterLayerType";

export interface Props {
    chosenLayerProps: ChosenLayerProps;
    layerType: string;
    min?: number;
    max?: number;
    dateStr?: string;
}

// eslint-disable-next-line react/display-name
export const MapLegend: React.FC<Props> = memo((props) => {
    const {t, i18n} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const msUtils = useMeasureSystemUtils();

    let layerProps: RasterLayerProps | AggregatedLayerProps;
    let min: number;
    let max: number;
    const minVal = props.min ?? 0;
    const maxVal = props.max ?? 0;

    if (isRasterLayerType(props.layerType)) {
        layerProps = props.chosenLayerProps as RasterLayerProps;
        min = layerProps.value ? layerProps.value(minVal, msUtils) : minVal;
        max = layerProps.value ? layerProps.value(maxVal, msUtils) : maxVal;
    } else {
        layerProps = props.chosenLayerProps as AggregatedLayerProps;
    }

    const renderedLegend = layerProps.levels.map((level, idx) => (
        <Stack key={level.color} direction="row" gap="1rem" alignItems="center">
            <Box width="1.4rem" height="1.6rem" bgcolor={level.color}></Box>
            <Typography fontSize={14}>
                {isRasterLayerType(props.layerType)
                    ? (level.label ??
                      getRasterLayerLegendTick(
                          min,
                          max,
                          layerProps.levels.length,
                          idx,
                          (layerProps as RasterLayerProps).type,
                      ))
                    : getAggregatedLayerLegendTick(
                          layerProps as AggregatedLayerProps,
                          idx,
                          dateFormat,
                          t,
                          props.dateStr,
                      )}
            </Typography>
        </Stack>
    ));

    return (
        <Stack component={Paper} elevation={0} square sx={{borderRadius: "0.25rem", padding: "1.125rem"}} gap="1rem">
            <Stack direction="row" gap="2rem" alignItems="center">
                <Typography fontSize={16} fontWeight={500}>
                    {t(`map.map_layer_selection.layers.${layerProps.type}`)}
                </Typography>
                {i18n.exists(`help.popover.map.${layerProps.type}`) && (
                    <HelpPopover i18nKey={`help.popover.map.${layerProps.type}`} direction="left" placement="top" />
                )}
            </Stack>
            <Stack>{layerProps.isReversed ? renderedLegend.reverse() : renderedLegend}</Stack>
            {layerProps.units && (
                <Typography fontSize={16} fontWeight={500}>
                    {layerProps.units(msUtils)}
                </Typography>
            )}
        </Stack>
    );
});

const getRasterLayerLegendTick = (
    min: number,
    max: number,
    levels: number,
    level: number,
    layerType: RasterLayerType,
) => {
    const value = level === levels - 1 ? max : min + ((max - min) / (levels - 1)) * level;

    return Number(value).toLocaleString("fullwide", {
        useGrouping: false,
        ...getRasterLayerLegendFormatOptions(layerType),
    });
};

const getRasterLayerLegendFormatOptions = (layerType: RasterLayerType): Intl.NumberFormatOptions => {
    if (
        layerType === RasterLayerType.DRY_MATTER_PRODUCTION ||
        layerType === RasterLayerType.DRY_MATTER_PRODUCTION_CUMULATIVE
    ) {
        return {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        };
    }

    return {
        minimumFractionDigits: 2,
    };
};

const getAggregatedLayerLegendTick = (
    layerProps: AggregatedLayerProps,
    idx: number,
    dateFormat: DateFormatType,
    t: TFunction,
    dateStr?: string,
): string => {
    const value = idx;
    const date = dateStr ? dayjs(dateStr, "YYYYMMDD").add(1, "day") : undefined;
    return layerProps?.getLegendTick ? layerProps.getLegendTick(value, dateFormat, date, t) : "";
};
